import React, { useMemo } from "react";
import { Box, Paper, Typography, Grid } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceLine,
  Cell,
} from "recharts";

const MESES = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "10", "11", "12"];

const fmtAxis = (v) => {
  if (typeof v !== "number" || Number.isNaN(v)) return v;
  const abs = Math.abs(v);
  if (abs >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${Math.round(v / 1_000)}k`;
  return String(Math.round(v));
};

const ChartTooltip = ({ active, payload, label, formatearMiles }) => {
  if (!active || !payload?.length) return null;
  return (
    <Paper sx={{ p: 1.25 }} elevation={4}>
      <Typography variant="caption" sx={{ fontWeight: 800, display: "block", mb: 0.5 }}>
        {label}
      </Typography>
      {payload.map((entry) => (
        <Typography
          key={entry.name}
          variant="caption"
          display="block"
          sx={{ color: entry.color }}
        >
          {entry.name}: {formatearMiles(entry.value ?? 0)}
        </Typography>
      ))}
    </Paper>
  );
};

const chartPaperSx = (theme) => ({
  p: { xs: 1.25, sm: 1.5 },
  borderRadius: 2.5,
  border: "1px solid",
  borderColor: alpha(theme.palette.divider, 0.85),
  backgroundColor: alpha(theme.palette.background.paper, 0.98),
  boxShadow:
    theme.palette.mode === "dark"
      ? "0 6px 18px rgba(0,0,0,0.28)"
      : "0 6px 16px rgba(15,23,42,0.06)",
  minWidth: 0,
  height: "100%",
});

const buildSeriesFromResultado = (source) => {
  if (!source) return [];
  let acumReal = 0;
  let acumPpto = 0;
  return MESES.map((mes, i) => {
    const row = source[mes] || { real: 0, estimate: 0, diff: 0 };
    acumReal += row.real || 0;
    acumPpto += row.estimate || 0;
    return {
      mes: `M${i + 1}`,
      real: row.real || 0,
      ppto: row.estimate || 0,
      diff: row.diff || 0,
      acumReal,
      acumPpto,
    };
  });
};

const PygResultadoFinalCharts = ({
  resultadoFinal,
  margenMensual,
  formatearMiles,
}) => {
  const theme = useTheme();

  const chartData = useMemo(
    () => buildSeriesFromResultado(resultadoFinal),
    [resultadoFinal],
  );

  const margenChartData = useMemo(
    () => buildSeriesFromResultado(margenMensual),
    [margenMensual],
  );

  const colorReal = theme.palette.info.main;
  const colorPpto = theme.palette.error.main;
  const colorPos = theme.palette.success.main;
  const colorNeg = theme.palette.error.main;
  const gridStroke = alpha(theme.palette.divider, 0.9);
  const tooltipProps = { formatearMiles };

  if (!chartData.length) return null;

  return (
    <Box sx={{ mb: 1.5 }}>
      <Grid container spacing={1.5}>
        <Grid item xs={12} lg={6}>
          <Paper sx={chartPaperSx(theme)}>
            <Typography variant="subtitle2" fontWeight={800} gutterBottom>
              Resultado final: Real vs presupuesto por mes
            </Typography>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={chartData} margin={{ top: 8, right: 8, left: 4, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} />
                <XAxis dataKey="mes" tick={{ fontSize: 11 }} />
                <YAxis tickFormatter={fmtAxis} tick={{ fontSize: 11 }} width={48} />
                <Tooltip content={<ChartTooltip {...tooltipProps} />} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <ReferenceLine y={0} stroke={theme.palette.text.disabled} />
                <Bar dataKey="real" name="Real" fill={colorReal} radius={[3, 3, 0, 0]} />
                <Bar dataKey="ppto" name="Ppto" fill={colorPpto} radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        <Grid item xs={12} lg={6}>
          <Paper sx={chartPaperSx(theme)}>
            <Typography variant="subtitle2" fontWeight={800} gutterBottom>
              Desviación mensual (Real − Ppto)
            </Typography>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={chartData} margin={{ top: 8, right: 8, left: 4, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} />
                <XAxis dataKey="mes" tick={{ fontSize: 11 }} />
                <YAxis tickFormatter={fmtAxis} tick={{ fontSize: 11 }} width={48} />
                <Tooltip content={<ChartTooltip {...tooltipProps} />} />
                <ReferenceLine y={0} stroke={theme.palette.text.disabled} />
                <Bar dataKey="diff" name="Desviación" radius={[3, 3, 0, 0]}>
                  {chartData.map((entry) => (
                    <Cell
                      key={entry.mes}
                      fill={entry.diff >= 0 ? colorPos : colorNeg}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        <Grid item xs={12} lg={6}>
          <Paper sx={chartPaperSx(theme)}>
            <Typography variant="subtitle2" fontWeight={800} gutterBottom>
              Resultado acumulado: Real vs presupuesto
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
              Suma progresiva del resultado final (contable + proyecciones y pedidos)
            </Typography>
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={chartData} margin={{ top: 8, right: 16, left: 4, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} />
                <XAxis dataKey="mes" tick={{ fontSize: 11 }} />
                <YAxis tickFormatter={fmtAxis} tick={{ fontSize: 11 }} width={52} />
                <Tooltip content={<ChartTooltip {...tooltipProps} />} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <ReferenceLine y={0} stroke={theme.palette.text.disabled} />
                <Line
                  type="monotone"
                  dataKey="acumReal"
                  name="Acum. Real"
                  stroke={colorReal}
                  strokeWidth={2.5}
                  dot={{ r: 3 }}
                  activeDot={{ r: 5 }}
                />
                <Line
                  type="monotone"
                  dataKey="acumPpto"
                  name="Acum. Ppto"
                  stroke={colorPpto}
                  strokeWidth={2}
                  strokeDasharray="6 4"
                  dot={{ r: 2 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {margenChartData.length > 0 ? (
          <Grid item xs={12} lg={6}>
            <Paper sx={chartPaperSx(theme)}>
              <Typography variant="subtitle2" fontWeight={800} gutterBottom>
                Margen comercial mensual: Real vs presupuesto
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                Ventas + servicios + variación exist. + trabajos + aprovisionamientos +
                proyecciones y pedidos
              </Typography>
              <ResponsiveContainer width="100%" height={240}>
                <BarChart
                  data={margenChartData}
                  margin={{ top: 8, right: 8, left: 4, bottom: 4 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} />
                  <XAxis dataKey="mes" tick={{ fontSize: 11 }} />
                  <YAxis tickFormatter={fmtAxis} tick={{ fontSize: 11 }} width={52} />
                  <Tooltip content={<ChartTooltip {...tooltipProps} />} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <ReferenceLine y={0} stroke={theme.palette.text.disabled} />
                  <Bar dataKey="real" name="Real" fill={colorReal} radius={[3, 3, 0, 0]} />
                  <Bar dataKey="ppto" name="Ppto" fill={colorPpto} radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>
        ) : null}
      </Grid>
    </Box>
  );
};

export default PygResultadoFinalCharts;
